import moment from 'moment-timezone';

import type { CSQueryPayload } from '../types/CSQueryPayload';
import type { CSCustomProperty } from '../types/CSCustomProperty';
import type { CSMultiSeries } from '../types/CSMultiSeries';
import type { CSRange } from '../types/CSRange';
import type { CSRangeConfig } from '../types/CSRangeConfig';
import type { CSRules } from '../types/CSRules';

class CSQuery implements CSQueryPayload {
    queryType: string;
    apiToken: string;
    browserTimezone: string;
    rangeConfig: CSRangeConfig;
    rules: CSRules;
    range: CSRange;
    shouldCalculateRangeBuckets: boolean = false;
    multiSeries?: CSMultiSeries;
    groupBys?: CSCustomProperty[];

    constructor(queryType: string) {
        this.queryType = queryType;

        // Set defaults
        this.apiToken = '';
        this.browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        this.rangeConfig = {
            intervalPreset: 'this-month',
            comparePreset: 'previous-interval',
            period: 'weekly',
            includePartial: true,
        };
        this.rules = {
            operator: 'AND',
            tag: null,
            locked: false,
            removable: true,
            rules: [],
        };
        this.range = {
            startDate: moment().valueOf(),
            endDate: moment().valueOf(),
            period: 'daily',
        };
    }

    setApiToken(token: string): this {
        this.apiToken = token;
        return this;
    }

    setBrowserTimezone(timezone: string): this {
        this.browserTimezone = timezone;
        return this;
    }

    setRangeConfig(config: CSRangeConfig): this {
        this.rangeConfig = config;
        return this;
    }

    setRules(rules: CSRules): this {
        this.rules = rules;
        return this;
    }

    setRange(range: CSRange): this {
        this.range = range;
        return this;
    }

    setMultiSeries(multiSeries?: CSMultiSeries): this {
        this.multiSeries = multiSeries;
        return this;
    }

    setGroupBys(groupBys?: CSCustomProperty[]): this {
        this.groupBys = groupBys;
        return this;
    }

    build(): CSQueryPayload {
        return {
            queryType: this.queryType,
            apiToken: this.apiToken,
            browserTimezone: this.browserTimezone,
            rangeConfig: this.rangeConfig,
            rules: this.rules,
            range: {
                ...this.range,
                ...(this.shouldCalculateRangeBuckets === true
                    ? { buckets: this.calculateRangeBuckets(this.range) }
                    : {}),
            },
            multiSeries: this.multiSeries,
            groupBys: this.groupBys,
        };
    }

    buildUrl(baseUrl: string): string {
        return '';
    }

    public calculateRangeBuckets(range: CSRange): number[] {
        const { startDate, endDate, period } = range;
        const buckets: number[] = [];
        let cursor = moment(startDate).startOf('day');
        const endMoment = moment(endDate);
        const unit = this.getMomentUnit(period);

        buckets.push(cursor.valueOf());
        while (cursor.add(1, unit).valueOf() <= endMoment.valueOf()) {
            buckets.push(cursor.valueOf());
        }

        return buckets;
    }

    private getMomentUnit(period: string): moment.unitOfTime.DurationConstructor {
        switch (period) {
            case 'hourly':
                return 'hour';
            case 'daily':
                return 'day';
            case 'weekly':
                return 'week';
            case 'monthly':
                return 'month';
            default:
                throw new Error(`Unsupported period: ${period}`);
        }
    }
}

export { CSQuery };
